import React, {useEffect, useMemo, useState} from "react";
import {Col, Form, Input, Radio, Row, Spin} from "antd";
import FilterDialog from "../../../components/query-builder/components/FilterDialogs/FilterDialog";
import {emptyUserQuery, QueryBuilderContext} from "../../../components/query-builder/QueryBuilderContext";
import {selectFields} from "../../../containers/QueryBuilder/queryBuilderSlice";
import {useAppSelector} from "../../../hooks";
import {Criterion, CriterionPcad, CriterionPcadValues} from "../../HealthcareUsageReport/types/CohortQuery";
import {UserQuery} from "../../QueryBuilder/types/UserQuery";
import {QueryField} from "../../QueryBuilder/types/Query";
import {LabelAndValue} from "../../../components/create/types/LabelAndValue";
import {Field} from "../../QueryBuilder/types/Field";
import ToolTip from "../../../components/common/ToolTip";

const CLINICAL_EVENT_FIELD_NAME = "clinicalevent_opcrdc";
const CLINICAL_EVENT_CUSTOM_FIELD_NAME = "clinicalevent_custom_opcrdc";
const CLINICAL_INDICATION_FIELD_NAME = "diag_opcrdc";
const TREATMENT_VMP_FIELD_NAME = "vmp_opcrdt";
const TREATMENT_CUSTOM_FIELD_NAME = "treatment_custom_opcrdt";


const CRITERION_DATA_FIELD_CLINICAL_EVENT = "clinicalEvent";
const CRITERION_DATA_FIELD_CLINICAL_EVENT_CUSTOM = "clinicalEventCustom";
const CRITERION_DATA_FIELD_CLINICAL_INDICATION = "clinicalIndication";
const CRITERION_DATA_FIELD_TREATMENT_VMP = "treatmentVMP";
const CRITERION_DATA_FIELD_TREATMENT_CUSTOM = "treatmentCustom";

const valueMapping : Map<string, string> = (() => {
    const map = new Map<string, string>();
    map.set(CLINICAL_EVENT_FIELD_NAME, CRITERION_DATA_FIELD_CLINICAL_EVENT);
    map.set(CLINICAL_INDICATION_FIELD_NAME, CRITERION_DATA_FIELD_CLINICAL_INDICATION);
    map.set(CLINICAL_EVENT_CUSTOM_FIELD_NAME, CRITERION_DATA_FIELD_CLINICAL_EVENT_CUSTOM);
    map.set(TREATMENT_VMP_FIELD_NAME, CRITERION_DATA_FIELD_TREATMENT_VMP);
    map.set(TREATMENT_CUSTOM_FIELD_NAME, CRITERION_DATA_FIELD_TREATMENT_CUSTOM);
    return map;
})();

const criterionCodesToQueryFilters : Map<string, string> = (() => {
    const map = new Map<string, string>();
    map.set(CRITERION_DATA_FIELD_CLINICAL_EVENT, CLINICAL_EVENT_FIELD_NAME);
    map.set(CRITERION_DATA_FIELD_CLINICAL_INDICATION, CLINICAL_INDICATION_FIELD_NAME);
    map.set(CRITERION_DATA_FIELD_CLINICAL_EVENT_CUSTOM, CLINICAL_EVENT_CUSTOM_FIELD_NAME);
    map.set(CRITERION_DATA_FIELD_TREATMENT_VMP, TREATMENT_VMP_FIELD_NAME);
    map.set(CRITERION_DATA_FIELD_TREATMENT_CUSTOM, TREATMENT_CUSTOM_FIELD_NAME);
    return map;
})();

export const userQueryToCriterionValues = (userQuery: UserQuery, queryCodeToCriterionField: Map<string, string>) : CriterionPcadValues => {
    const fieldsToMap = Array.from(queryCodeToCriterionField.keys());

    const queryCodeToValues = fieldsToMap.reduce((prev, code) => {
        const valuesForFieldString = userQuery.query.filters.find(filter => filter.code === code)?.value;
        const valuesForField = valuesForFieldString ? valuesForFieldString.split(",") : undefined;
        const labelsForValue = userQuery.query.filterLabels.find(filter => filter.field === code)?.values;

        let labelAndValues : LabelAndValue[] | undefined = undefined;
        if (labelsForValue && valuesForField && labelsForValue.length === valuesForField.length) {
            labelAndValues = labelsForValue.map((label, i, arr) => {
                return {label: labelsForValue[i], value: valuesForField[i]} as LabelAndValue;
            });
        }
        if (labelsForValue && valuesForField && valuesForField.length > labelsForValue.length) {
            labelAndValues = [{
                label: labelsForValue.join(", "),
                value: valuesForField.join(",")
            }]
        }
        prev[code] = labelAndValues;
        return prev;
    }, {} as any);

    const criterionValues = Object.keys(queryCodeToValues).reduce((prev, key) => {
        const criterionField = queryCodeToCriterionField.get(key)!;
        prev[criterionField] = queryCodeToValues[key];
        return prev;
    }, {} as any)
    return criterionValues;
}

export const criterionToUserQuery = (criterion: Criterion, fields: {[p: string]: Field}, criterionCodesToQueryFilters: Map<string, string>) => {
    const userQuery = emptyUserQuery();

    Array.from(criterionCodesToQueryFilters.values()).forEach(filterCode => {
        const filterSearch = userQuery.query.filters.find(filter => filter.code === filterCode);
        if (filterSearch === undefined && fields[filterCode] !== undefined) {
            userQuery.query.filters.push(JSON.parse(JSON.stringify(fields[filterCode])))
        }
    });

    Array.from(criterionCodesToQueryFilters.keys()).forEach(criterionKey => {
        const labelsAndValues: LabelAndValue[] | undefined = criterion[criterionKey];
        const queryKey = criterionCodesToQueryFilters.get(criterionKey)!;
        const queryField = userQuery.query.filters.find(filter => filter.code === queryKey);
        const values = labelsAndValues?.map(labelAndValue => labelAndValue.value)
            .reduce((prev, curr) => {
                return prev + "," + curr
        });
        const labels = labelsAndValues?.map(labelsAndValues => labelsAndValues.label);
        queryField && (queryField.value = values);
        let filterLabel = userQuery.query.filterLabels.find(filterLabel => filterLabel.field === queryKey);
        if (filterLabel === undefined && values && labels) {
            filterLabel = {
                field: queryKey,
                title: fields[queryKey].name,
                values: labels
            }
            userQuery.query.filterLabels.push(filterLabel);
        }
        if (filterLabel !== undefined) {
            filterLabel.values = labels!;
        }
    });

    return userQuery;
}

const EditCriterionPcad = ({criterion, onChange, onFormReady}: {criterion: Criterion, onChange: (criteria: CriterionPcad) => void, onFormReady: (ready: boolean) => void}) => {

    const fields = useAppSelector(selectFields);
    const [disableModalOpen, setDisableModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [currentField, setCurrentField] = useState<{ field: QueryField } | undefined>(undefined);
    const [dataset, setDataset] = useState<"clinical"|"treatment">(criterion.dataset || "clinical")

    const clinicalEventField = useMemo(() => fields && JSON.parse(JSON.stringify(fields[CLINICAL_EVENT_FIELD_NAME])), [fields]) as QueryField;
    const clinicalIndicationField = useMemo(() => fields && JSON.parse(JSON.stringify(fields[CLINICAL_INDICATION_FIELD_NAME])), [fields]) as QueryField;
    const clinicalCustomCodeList : QueryField | undefined = useMemo(() => fields && fields[CLINICAL_EVENT_CUSTOM_FIELD_NAME] && JSON.parse((JSON.stringify(fields[CLINICAL_EVENT_CUSTOM_FIELD_NAME]))), [fields]);
    const treatmentVmpField = useMemo(() => fields && JSON.parse(JSON.stringify(fields[TREATMENT_VMP_FIELD_NAME])), [fields]) as QueryField;
    const treatmentCustomCodeList : QueryField | undefined = useMemo(() => fields && fields[TREATMENT_CUSTOM_FIELD_NAME] && JSON.parse((JSON.stringify(fields[TREATMENT_CUSTOM_FIELD_NAME]))), [fields]);
    /**
     * Had to disable the inputs when modal closes as it focuses on the input after close,
     * and causes the modal to open in an infinite loop.
     * A smaller number (i.e. 10ms) is too short and modal reopens
     */
    useEffect(() => {
        const timeout = setTimeout(() => {
            setDisableModalOpen(false);
        }, 300);
        return () => {
            clearTimeout(timeout);
        }
    }, [disableModalOpen])

    const isReady = (userQuery: UserQuery) => {
        return userQuery.query.filterLabels.filter(filterLabel => {
            return filterLabel.values.length > 0
        }).length > 0;
    };

    const userQuery = useMemo(() => {
        return fields && criterionToUserQuery(criterion, fields, criterionCodesToQueryFilters);
    }, [fields, criterion]);

    useEffect(() => {
        let mounted = true;
        if (userQuery) {
            mounted && onFormReady(isReady(userQuery));
        }
        return () => {
            mounted = false;
        }
    })

    useEffect(() => {
        if (!userQuery) {
            return;
        }
        const criterionValues = userQueryToCriterionValues(userQuery, valueMapping);

        if (dataset === "clinical") {
            criterionValues.treatmentVMP = undefined;
        }
        if (dataset === "treatment") {
            criterionValues.clinicalEvent = undefined;
            criterionValues.clinicalIndication = undefined;
            criterionValues.clinicalEventCustom = undefined;
        }
        _onChange(criterionValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataset])

    const handleChange = (uq: UserQuery) => {
        const criterionValues = userQueryToCriterionValues(uq, valueMapping) as CriterionPcadValues
        _onChange(criterionValues);
    }

    const _onChange = (criterionValues : CriterionPcadValues) => {
        setIsModalOpen(false);
        setCurrentField(undefined);

        criterionValues.dataset = dataset;

        const criteriaUpdate: CriterionPcad = {
            id: criterion.id,
            description: undefined,
            operator: criterion.operator,
            ...criterionValues
        }
        onChange(criteriaUpdate)
    }

    const closeModal = (open: boolean) => {
        setDisableModalOpen(true);
        setIsModalOpen(open);
    }

    const getFilterLabelValuesFromUserQuery = (userQuery: UserQuery, filterField: String) => {
        const filterSearch = userQuery.query.filterLabels
            .find(filterLabel => filterLabel.field === filterField);
        return filterSearch && filterSearch.values.join(", ");
    }

    const clinicalEventValues = userQuery && getFilterLabelValuesFromUserQuery(userQuery, CLINICAL_EVENT_FIELD_NAME);
    const clinicalIndicationValues = userQuery && getFilterLabelValuesFromUserQuery(userQuery, CLINICAL_INDICATION_FIELD_NAME);
    const clinicalIndicationsCustomValues = userQuery && getFilterLabelValuesFromUserQuery(userQuery, CLINICAL_EVENT_CUSTOM_FIELD_NAME);
    const treatmentVmpValues = userQuery && getFilterLabelValuesFromUserQuery(userQuery, TREATMENT_VMP_FIELD_NAME);
    const treatmentCustomValues = userQuery && getFilterLabelValuesFromUserQuery(userQuery, TREATMENT_CUSTOM_FIELD_NAME);

    const readyForInteraction = !!fields;
    const disableAllInputs = disableModalOpen;

    if (!readyForInteraction) {
        return <Spin/>
    }

    const shouldDisplayCustomClinicalCodes = !!(fields && fields[CLINICAL_EVENT_CUSTOM_FIELD_NAME])
    const shouldDisplayCustomTreatmentCodes = !!(fields && fields[TREATMENT_CUSTOM_FIELD_NAME])

    return <>
        <Row className="sub-section" style={{width: "100%", maxWidth: "1000px"}}>
            <Col style={{width: "100%"}}>
                <div className="border-bottom">Primary Care Activity Data</div>
                {!userQuery && <Spin/>}
                {/* @ts-ignore */}
                {userQuery && <QueryBuilderContext.Provider value={{ userQuery , setUserQuery: handleChange,  currentField, setCurrentField: () => {}, isFilterModalOpen: isModalOpen, setIsFilterModalOpen: closeModal}}>
                    <div>
                        <Row align={"middle"}>
                            <Form.Item
                                label="Select Fields"
                            >
                                <Radio.Group style={{ width: '100%' }}
                                             onChange={event => {setDataset(event.target.value)}}
                                             options={[
                                                 {label: "Clinical", value: "clinical"},
                                                 {label: "Treatment", value: "treatment"}
                                             ]}
                                             value={dataset}
                                />
                            </Form.Item>
                            <ToolTip content={<span>Please note that Treatment Criteria cannot be mixed with Clinical Events or Clinical Indications within the same criteria</span>} />

                        </Row>
                    </div>
                    <div>
                        <Row>
                            <Form.Item label={"Clinical Event (SNOMED)"} />
                        </Row>
                        <Row>
                            <Input.Search
                                disabled={disableAllInputs || dataset === "treatment"}
                                onFocus={() => {setCurrentField({field:clinicalEventField}); setIsModalOpen(true)}}
                                value={clinicalEventValues}
                                placeholder={"Search Clinical Event"}
                            />
                            {currentField?.field.code === CLINICAL_EVENT_FIELD_NAME  && <FilterDialog />}
                        </Row>
                    </div>

                    <div>
                        <Row>
                            <Form.Item label={"Clinical Indication"} style={{width: "100%"}} />
                        </Row>

                        <Row>
                            <Input.Search
                                disabled={disableAllInputs || dataset === "treatment"}
                                onFocus={() => {setCurrentField({field:clinicalIndicationField}); setIsModalOpen(true)}}
                                value={clinicalIndicationValues}
                                placeholder={"Search Clinical Indications"}
                            />
                            {currentField?.field.code === CLINICAL_INDICATION_FIELD_NAME  && <FilterDialog />}
                        </Row>
                    </div>

                    {shouldDisplayCustomClinicalCodes && <div>
                        <Row>
                            <Form.Item label={"Clinical Custom Code List"} style={{width: "100%"}} />
                        </Row>

                        <Row>
                            <Input.Search
                                disabled={disableAllInputs || dataset === "treatment"}
                                onFocus={() => {setCurrentField({field:clinicalCustomCodeList!}); setIsModalOpen(true)}}
                                value={clinicalIndicationsCustomValues}
                                placeholder={"Add Clinical Custom Code List"}
                            />
                            {currentField?.field.code === CLINICAL_EVENT_CUSTOM_FIELD_NAME  && <FilterDialog />}
                        </Row>
                    </div>}

                    <div>
                        <Row>
                            <Form.Item label={"Treatment VMP"}/>
                        </Row>
                        <Row>
                            <Input.Search disabled={ disableAllInputs || dataset === "clinical"}
                                  onFocus={() => {setCurrentField({field:treatmentVmpField}); setIsModalOpen(true)}}
                                  value={treatmentVmpValues}
                                  placeholder={"Search Treatment VMP"}
                            />
                            {currentField?.field.code === TREATMENT_VMP_FIELD_NAME && <FilterDialog/>}
                        </Row>
                    </div>
                    {shouldDisplayCustomTreatmentCodes && <div>
                        <Row>
                            <Form.Item label={"Treatment Custom Code List"} style={{width: "100%"}} />
                        </Row>

                        <Row>
                            <Input.Search
                                disabled={disableAllInputs || dataset === "clinical"}
                                onFocus={() => {setCurrentField({field:treatmentCustomCodeList!}); setIsModalOpen(true)}}
                                value={treatmentCustomValues}
                                placeholder={"Add Treatment Custom Code List"}
                            />
                            {currentField?.field.code === TREATMENT_CUSTOM_FIELD_NAME  && <FilterDialog />}
                        </Row>
                    </div>}
                </QueryBuilderContext.Provider>
                }
            </Col>
        </Row>
    </>
}

export default  EditCriterionPcad;